import * as React from "react";
import { Autocomplete, Checkbox, FormControlLabel, InputAdornment, TextField } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import ConfirmModal from "../confirm.modal";
import type { ModalComponentProps } from "../../../types/modal.types";
import type { newQuotationForm } from "../../../lib/apis/dtos/request/quotation.request";
import type { partnerQuotationReferenceForm } from "../../../lib/apis/dtos/request/partner.request";
import { usePartnerStoreList } from "../../../lib/apis/hooks/partner.hook";
import { getPartnerQuotationReference } from "../../../lib/apis/v1/partner.service";
import { mergeImportedQuotation } from "../../../utils/quotation-import.utils";
import FailSnackbar from "../../ui/snackbar/fail.component";
import SuccessSnackbar from "../../ui/snackbar/success.component";

interface QuotationImportModalProps extends ModalComponentProps {
  /** 현재 편집 중인 폼 — 메타 정보 유지용 */
  currentForm: newQuotationForm;
  /** 가져온 결과를 편집 폼에 반영 (제출 아님) */
  onImport: (next: newQuotationForm) => void;
  /** 제휴 업체에 연결된 공급업체 — 있으면 대상 지점 기본 선택 */
  defaultStore?: StoreOption | null;
}

interface StoreOption {
  id: number;
  storeName: string;
}

/** 일괄 가감 항목 키 — API 파라미터 use{Key}/add{Key} 와 대응 */
type AdjustKey = "Main" | "Kids" | "Usim";

interface AdjustState {
  use: boolean;
  /** 입력 문자열 (만원, 소수점 한 자리). "" 이면 원본 그대로 */
  text: string;
}

const ADJUST_ROWS: { key: AdjustKey; label: string }[] = [
  { key: "Main", label: "주요폰 최종금액 일괄" },
  { key: "Kids", label: "키즈폰 최종금액 일괄" },
  { key: "Usim", label: "유심 페이백 일괄" },
];

const INIT_ADJUST: Record<AdjustKey, AdjustState> = {
  Main: { use: false, text: "" },
  Kids: { use: false, text: "" },
  Usim: { use: false, text: "" },
};

/** "+12.1" / "-3" / "12" → 숫자. 비어 있거나 부호만이면 undefined */
const parseAmount = (raw: string): number | undefined => {
  const n = Number(raw.replace(/,/g, ""));
  return raw === "" || raw === "+" || raw === "-" || !Number.isFinite(n) ? undefined : n;
};

/** 부호 포함, 정수부 임의 + 소수점 한 자리까지만 허용 */
const AMOUNT_PATTERN = /^[-+]?\d*(\.\d{0,1})?$/;

/** ±1 만원 — 소수점 한 자리 유지, 부동소수 오차 제거 */
const stepAmount = (raw: string, delta: 1 | -1): string => {
  const n = parseAmount(raw) ?? 0;
  return (Math.round((n + delta) * 10) / 10).toString();
};

/**
 * 시세표 가져오기 — 다른 업체(지점)의 시세표를 항목별(주요폰·키즈폰·유심) 만원 단위 ± 가감해서 현재 폼에 복사.
 * 가감 계산은 백엔드(GET /v1/partner/quotation/reference/{storeId}) 가 담당하고,
 * 서버에 제출하지 않는다.
 */
export default function QuotationImportModal({ id, onClose, currentForm, onImport, defaultStore = null }: QuotationImportModalProps) {
  const [selected, setSelected] = React.useState<StoreOption | null>(defaultStore);
  const [adjust, setAdjust] = React.useState<Record<AdjustKey, AdjustState>>(INIT_ADJUST);
  const [useAddService, setUseAddService] = React.useState(true);
  const [loading, setLoading] = React.useState(false);

  // 공급업체 후보 목록 — 제휴 업체 공급업체 선택과 같은 소스 (GET /v1/partner/store)
  const { data, isSuccess } = useQuery(usePartnerStoreList());
  const options: StoreOption[] = (data?.result || []).map((s) => ({ id: s.id, storeName: s.storeName }));

  const setUse = (key: AdjustKey, use: boolean) => setAdjust((prev) => ({ ...prev, [key]: { ...prev[key], use } }));

  const setText = (key: AdjustKey, raw: string) => {
    const val = raw.replace(/,/g, "");
    if (AMOUNT_PATTERN.test(val)) setAdjust((prev) => ({ ...prev, [key]: { ...prev[key], text: val } }));
  };

  const step = (key: AdjustKey, delta: 1 | -1) =>
    setAdjust((prev) => ({ ...prev, [key]: { ...prev[key], text: stepAmount(prev[key].text, delta) } }));

  /** 체크된 항목만 add 값 전달, 해제된 항목은 use=false 만 전달 */
  const buildParams = (): Omit<partnerQuotationReferenceForm, "storeId"> => ({
    useMain: adjust.Main.use,
    addMain: adjust.Main.use ? parseAmount(adjust.Main.text) : undefined,
    useKids: adjust.Kids.use,
    addKids: adjust.Kids.use ? parseAmount(adjust.Kids.text) : undefined,
    useUsim: adjust.Usim.use,
    addUsim: adjust.Usim.use ? parseAmount(adjust.Usim.text) : undefined,
    useAddService,
  });

  const handleImport = async () => {
    if (!selected) return;
    setLoading(true);
    try {
      const res = await getPartnerQuotationReference({ storeId: selected.id, ...buildParams() });
      if (res.error || !res.result) {
        FailSnackbar(res.message || "시세표 불러오기 실패");
        return;
      }
      onImport(mergeImportedQuotation(res.result, currentForm));
      SuccessSnackbar(`${selected.storeName} 시세표를 가져왔습니다. 확인 후 "시세표 제출"을 눌러주세요.`);
      onClose?.();
    } catch {
      FailSnackbar("시세표 불러오기 실패");
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConfirmModal
      id={id}
      onClose={onClose}
      className="w-[900px]"
      title="시세표 가져오기"
      rightButton={{
        title: loading ? "가져오는 중..." : "시세표 가져오기",
        disabled: !selected || loading,
        variant: !selected || loading ? "disabled" : "primary",
        onClick: handleImport,
      }}
    >
      <p className="text-[16px] py-4 text-gray-60">가져올 업체의 최근 제출 시세표를 현재 시세표에 복사합니다.</p>

      <div className="flex-row gap-4 items-start">
        <Autocomplete
          className="w-[380px]"
          options={options}
          loading={!isSuccess}
          value={selected}
          getOptionLabel={(o) => o.storeName}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          onChange={(_, v) => setSelected(v)}
          disablePortal
          slotProps={{ popper: { sx: { zIndex: 9999 } } }}
          renderOption={(props, option) => (
            <li {...props} key={option.id}>
              {option.storeName}
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="가져올 업체 선택" placeholder="지점명으로 검색" slotProps={{ inputLabel: { shrink: true } }} />
          )}
        />
        <span className="text-[20px] font-semibold leading-[56px]">에서</span>

        <div className="flex flex-col gap-1">
          {ADJUST_ROWS.map(({ key, label }) => (
            <AdjustRow
              key={key}
              label={label}
              state={adjust[key]}
              onUseChange={(use) => setUse(key, use)}
              onTextChange={(raw) => setText(key, raw)}
              onStep={(delta) => step(key, delta)}
            />
          ))}
        </div>
      </div>

      <FormControlLabel
        className="mt-4"
        label="부가서비스 포함하여 가져오기"
        control={<Checkbox checked={useAddService} onChange={(e) => setUseAddService(e.target.checked)} />}
      />

      <p className="mt-2 text-[13px] whitespace-pre-line text-black/60">
        {`*'만원' 단위로 입력해주세요. 소수점 한자리까지(천원 단위까지) 표기 가능합니다. 비워두면 원본 가격 그대로 복사됩니다.
*현재 입력한 값들이 있다면 모두 사라집니다.`}
      </p>
      <p className="mt-3 text-right text-[14px] text-[#B3261E]">※ 해당 가져오기는 제출이 아닌 복사입니다.</p>
    </ConfirmModal>
  );
}

interface AdjustRowProps {
  label: string;
  state: AdjustState;
  onUseChange: (use: boolean) => void;
  onTextChange: (raw: string) => void;
  onStep: (delta: 1 | -1) => void;
}

/** 체크박스 + 라벨 + [-] 입력 [+] 한 줄 — 체크 해제 시 입력·버튼 비활성 */
function AdjustRow({ label, state, onUseChange, onTextChange, onStep }: AdjustRowProps) {
  const disabled = !state.use;
  return (
    <div className="flex-row items-center">
      <FormControlLabel
        className="w-[230px] m-0"
        label={<span className="text-[16px]">{label}</span>}
        control={<Checkbox checked={state.use} onChange={(e) => onUseChange(e.target.checked)} />}
      />
      <StepButton sign="+" disabled={disabled} onClick={() => onStep(1)} ariaLabel={`${label} 1만원 증가`} />
      <TextField
        className="w-[110px] mx-2"
        value={state.text}
        disabled={disabled}
        onChange={(e) => onTextChange(e.target.value)}
        inputProps={{ maxLength: 7, inputMode: "decimal", style: { textAlign: "right" } }}
        slotProps={{
          input: {
            placeholder: "0",
            sx: { height: "36px" },
            endAdornment: <InputAdornment position="end">만원</InputAdornment>,
          },
        }}
      />
      <StepButton sign="-" disabled={disabled} onClick={() => onStep(-1)} ariaLabel={`${label} 1만원 감소`} />
    </div>
  );
}

interface StepButtonProps {
  sign: "+" | "-";
  disabled: boolean;
  ariaLabel: string;
  onClick: () => void;
}

/** 원형 ± 버튼 — 1만원 단위 증감 */
function StepButton({ sign, disabled, ariaLabel, onClick }: StepButtonProps) {
  return (
    <button
      type="button"
      disabled={disabled}
      aria-label={ariaLabel}
      onClick={onClick}
      className="w-[24px] h-[24px] rounded-full bg-gray-20 text-white text-[18px] leading-none flex-row justify-center items-center disabled:opacity-40 hover:bg-gray-40 disabled:hover:bg-gray-20"
    >
      {sign}
    </button>
  );
}
